import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import ChevronIcon from '@boltui/icons/src/components/ChevronIcon'
import * as Styled from './Accordion.styles'

const getChildOfType = (child, type) => child.type && child.type === type

const Accordion = ({ singleOpenOnly = false, 'data-test': dataTest, children }) => {
  let defaultActiveItem = null
  React.Children.map(children, (child, index) => {
    if (child.props.isOpen) {
      defaultActiveItem = index
    }
  })

  const [_isOpen, toggleOpen] = useState(false)
  const [_activeItem, toggleActive] = useState(defaultActiveItem)

  const onAccordionItemClick = (index) => {
    if (singleOpenOnly) {
      toggleOpen(false)
      toggleActive(index)
    }
  }
  const onToggleClick = () => {
    toggleActive(null)
    toggleOpen(!_isOpen)
  }

  const buttonText = _isOpen ? 'Collapse all' : 'Expand all'
  return (
    <>
      {!singleOpenOnly && children.length > 1 && (
        <Styled.ButtonAll data-test={`${dataTest}-button-toggle`} onClick={onToggleClick}>
          {buttonText}
        </Styled.ButtonAll>
      )}
      {React.Children.map(children, (child, index) => {
        const { 'data-test': childDataTest, onClick } = child.props
        const shouldItBeOpen = singleOpenOnly ? _activeItem === index : _isOpen
        const handleClick = (ItemIndex, ItemIsOpen) => {
          onAccordionItemClick(index)
          onClick(ItemIndex, ItemIsOpen)
        }
        return (
          <AccordionItem
            {...child.props}
            data-test={`${childDataTest}${index !== null ? `-${index}` : ''}`}
            isOpen={shouldItBeOpen}
            onClick={handleClick}
          />
        )
      })}
    </>
  )
}

Accordion.defaultProps = {
  singleOpenOnly: false,
  'data-test': 'component-accordion-wrapper'
}

Accordion.propTypes = {
  /**
   * Optional prop to specifiy whether the Accordion should only open one at a time
   */
  singleOpenOnly: PropTypes.bool
}

const ExpandedContent = ({ children }) => children

ExpandedContent.propTypes = {
  /**
   * Specify the content of your Button
   */
  children: PropTypes.node
}

const AccordionItem = forwardRef(
  (
    {
      children,
      title,
      isOpen,
      hasBorderTop,
      hasBorderBottom,
      wrapperPadding,
      titleBodySpacing,
      toggleColor,
      customToggle,
      disabled,
      as: Element,
      onClick,
      'data-test': dataTest,
      index,
      noAnimation,
      showMoreLabel,
      secondaryCTA,
      footer,
      ...props
    },
    forwardedRef
  ) => {
    const [_isOpen, toggleOpen] = useState(isOpen)
    const [_isExpandedOpen, toggleExpanded] = useState(false)
    const [_setHeight, setHeightState] = useState('0px')
    useEffect(() => {
      toggleOpen(isOpen)
      setHeightState(`0px`)
    }, [isOpen])

    const content = []
    const expandedContent = []
    const container = useRef(null)

    useEffect(() => {
      setTimeout(
        () =>
          setHeightState(
            isOpen && container.current && container.current.scrollHeight
              ? `${container.current.scrollHeight}px`
              : '0px'
          ),
        0
      )
    }, [isOpen])

    useImperativeHandle(forwardedRef, () => ({
      updateHeight: () => {
        if (_isOpen) {
          setTimeout(
            () =>
              setHeightState(
                container.current && container.current.scrollHeight ? `${container.current.scrollHeight}px` : '0px'
              ),
            0
          )
        }
      }
    }))

    React.Children.forEach(children, (child) => {
      if (getChildOfType(child, ExpandedContent)) {
        expandedContent.push(child)
      } else {
        content.push(child)
      }
    })

    const toggleOpenfunc = () => {
      setHeightState(
        !_isOpen && container.current && container.current.scrollHeight ? `${container.current.scrollHeight}px` : '0px'
      )
      toggleOpen(!_isOpen)
      onClick(index, !_isOpen)
    }

    const toggleExpandedfunc = () => {
      toggleExpanded(!_isExpandedOpen)
      setHeightState(`none`)
    }

    const hasExpandedContent = expandedContent.length > 0
    const showExpandedContent = hasExpandedContent && _isExpandedOpen

    // Forces the open by default accordion to set an initial max height instead of being undefined/0px
    // If no animation force to have the initial max height
    const setMaxHeight = noAnimation && _isOpen ? 'initial' : _setHeight

    return (
      <Styled.Wrapper
        hasBorderTop={hasBorderTop}
        hasBorderBottom={hasBorderBottom}
        wrapperPadding={wrapperPadding}
        data-test={`${dataTest}${index !== null ? `-${index}` : ''}`}
        aria-expanded={_isOpen}
        aria-disabled={disabled}
        {...props}>
        <Styled.Button
          data-test={`${dataTest}-button-toggle`}
          disabled={disabled}
          onClick={toggleOpenfunc}
          aria-controls={`accordion-controls-${index}`}
          id={`accordion-controls-${index}`}>
          <Styled.Title as={Element} data-test={`${dataTest}-title`} role='heading' aria-level={1}>
            {title}
          </Styled.Title>
          {customToggle ? (
            React.cloneElement(customToggle, { isOpen: _isOpen })
          ) : (
            <Styled.Toggle isOpen={_isOpen}>
              <span className='sr-only'>{_isOpen ? 'Close' : 'Open'}</span>
              <ChevronIcon fill={toggleColor} width='24px' height='24px' dir='down' />
            </Styled.Toggle>
          )}
        </Styled.Button>
        {secondaryCTA}
        <Styled.Container
          ref={container}
          maxHeight={setMaxHeight}
          role='region'
          aria-labelledby={`accordion-controls-${index}`}
          noAnimation={noAnimation}>
          <Styled.InnerContainer data-test={`${dataTest}-content`} titleBodySpacing={titleBodySpacing}>
            {content}
            {showExpandedContent && <div data-test={`${dataTest}-content-expanded`}>{expandedContent}</div>}
          </Styled.InnerContainer>
        </Styled.Container>
        {_isOpen && !_isExpandedOpen && hasExpandedContent && (
          <Styled.ButtonLink data-test={`${dataTest}-button-show-more`} onClick={toggleExpandedfunc}>
            {showMoreLabel}
          </Styled.ButtonLink>
        )}
        {_isOpen && _isExpandedOpen && hasExpandedContent && (
          <Styled.ButtonLink data-test={`${dataTest}-button-show-less`} onClick={toggleExpandedfunc}>
            Show less
          </Styled.ButtonLink>
        )}
        {footer ? React.cloneElement(footer, { isOpen: _isOpen, toggleOpenfunc }) : null}
      </Styled.Wrapper>
    )
  }
)

AccordionItem.propTypes = {
  /**
   * Specify the content of your Accordion
   */
  children: PropTypes.node,

  /**
   * Optional prop to change the Show More label
   */
  showMoreLabel: PropTypes.string,

  /**
   * Specify an optional data-test attribute to be added to your Accordion for e2e testing
   */
  'data-test': PropTypes.string,

  /**
   * Optional Prop to specifiy whether the Accordion should be open by default, or not
   */
  isOpen: PropTypes.bool,

  /**
   * Optional prop to specifiy whether the Accordion should be disabled, or not
   */
  disabled: PropTypes.bool,

  /**
   * Optional prop to specify the role of the Accordion
   */
  as: PropTypes.node,

  /**
   * Optional prop to pass through onClick events when Accordion is clicked
   */
  onClick: PropTypes.func,

  /**
   * Optional prop to specify the padding around the Accordion
   */
  wrapperPadding: PropTypes.number,

  /**
   * Optional prop to specify the spacing between the Accordion title and body
   */
  titleBodySpacing: PropTypes.element,

  /**
   * Optional prop to specify the toggle colour
   */
  toggleColor: PropTypes.string,

  /**
   * Optional prop to provide a custom React element for the toggle
   */
  customToggle: PropTypes.node,

  /**
   * Optional prop to provide a secondaryCTA
   */
  secondaryCTA: PropTypes.node,

  /**
   * Optional prop to provide a footer
   */
  footer: PropTypes.node,

  /**
   * Optional prop to remove the animation
   */
  noAnimation: PropTypes.bool
}

AccordionItem.defaultProps = {
  showMoreLabel: 'Show more',
  disabled: false,
  as: 'h4',
  isOpen: false,
  onClick: /* istanbul ignore next */ () => {},
  'data-test': 'component-accordion',
  index: null,
  noAnimation: false
}

Accordion.Item = AccordionItem
Accordion.Expanded = ExpandedContent

export default Accordion
export { AccordionItem }
